import { LocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { TutorialService } from '../../tutorial/services/tutorial.service';

/**
 * Service to handle links generated through markdown parsing.
 * Taken from https://github.com/jfcere/ngx-markdown/issues/125
 */
@Injectable({ providedIn: 'root' })
export class AnchorService {

  /**
   * The constructor
   *
   * @param {LocationStrategy} locationStrategy
   * @param {ActivatedRoute} route
   * @param {Router} router
   * @param {TutorialService} tutorialService
   */
  public constructor(
    private locationStrategy: LocationStrategy,
    private route: ActivatedRoute,
    private router: Router,
    private tutorialService: TutorialService,
  ) { }

  /**
   * Intercept clicks on `HTMLAnchorElement` to use `Router.navigate()`
   * when `href` is an internal URL not handled by `routerLink` directive.
   *
   * @param event The event to evaluated for link click.
   *
   * @return void
   */
  public interceptClick(event: Event): void {

    let element = event.target;

    // Sometimes we have an image inside an anchor element.
    if (element instanceof HTMLImageElement) {
      element = element.parentElement;
    }

    if (element instanceof HTMLAnchorElement) {
      const href = element.getAttribute('href');
      if (href === null || this.isExternalUrl(href) || this.isRouterLink(element) || this.isMailUrl(href)) {
        return;
      }

      // Let any active shown tutorials know that they should close.
      this.tutorialService.tutorialToggleForPage.next(false);

      this.navigate(href);
      event.preventDefault();
    }
  }

  /**
   * Navigate to URL using angular `Router`.
   * @param url Destination path to navigate to.
   * @param replaceUrl If `true`, replaces current state in browser history.
   *
   * @return void
   */
  private navigate(url: string, replaceUrl = false): void {
    const urlTree = this.getUrlTree(url);
    this.router.navigated = false;
    this.router.navigateByUrl(urlTree, { replaceUrl });
  }

  /**
   * Get the URL tree.
   *
   * @param {string} url
   *
   * @returns {UrlTree}
   */
  private getUrlTree(url: string): UrlTree {
    const urlPath = this.stripFragment(url) || this.stripFragment(this.router.url);
    const urlFragment = this.router.parseUrl(url).fragment;
    return this.router.createUrlTree([urlPath], { relativeTo: this.route, fragment: urlFragment });
  }

  /**
   * Check for external URL.
   *
   * @param {string} url
   *
   * @returns {boolean}
   */
  private isExternalUrl(url: string): boolean {
    return /^(?!http(s?):\/\/).+$/.exec(url) == null;
  }

  /**
   * Check for mail URL.
   *
   * @param {string} url
   *
   * @returns {boolean}
   */
  private isMailUrl(url: string): boolean {
    return /^(?!mailto:)+$/.exec(url) == null;
  }

  /**
   * Check for RouterLink
   *
   * @param {HTMLAnchorElement} element
   *
   * @returns {boolean}
   */
  private isRouterLink(element: HTMLAnchorElement): boolean {
    return element.getAttributeNames().some(n => n.startsWith('_ngcontent'));
  }

  /**
   * Strips fragments from an URL.
   *
   * @param {string} url
   *
   * @returns {string}
   */
  private stripFragment(url: string): string {
    return /[^#]*/.exec(url)[0];
  }
}